/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

import { Typography as Text } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

import { SUPPORTED_MIMETYPES } from '../../constants';
import { useUserQuery } from '../../actions/UserActions';
import { useRateLimitsQuery } from '../../actions/WorkspaceActions';
import LinearProgress from '@mui/material/LinearProgress';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

function validMimetype(file) {
  const tp = file.type;

  if (!tp.startsWith('video/') && !tp.startsWith('audio/')) {
    return true;
  }

  if (SUPPORTED_MIMETYPES.includes(tp)) {
    return true;
  }

  const pth = file.path.toLowerCase();

  if (tp === '' && pth.endsWith('.wma')) {
    return true;
  }
  return false;
}

export default function File({
  files,
  onUpload,
  onError,
  onWarning,
  disabled,
  progress,
}) {
  const { t } = useTranslation();
  const [filesData, setFilesData] = useState([]);
  const { user } = useUserQuery();
  const isTrial = user?.workspace?.type === 'trial';
  const { rateLimits } = useRateLimitsQuery({
    enabled: !!user?.role && user?.role !== 'guest',
  });
  const uploading = Object.values(progress).some((x) => x.progress <= 100);

  const rateLimitLeft = rateLimits?.upload
    ? rateLimits.upload.limit - rateLimits.upload.value
    : 100;

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (
        user?.workspace?.type === 'trial' &&
        acceptedFiles?.length > rateLimitLeft
      ) {
        onError(
          t('Du har en max gräns på {{limit}} filer per timme', {
            limit: rateLimitLeft,
          })
        );
        return;
      }
      onUpload(acceptedFiles);

      const multi = acceptedFiles?.length > 1;
      for (let file of acceptedFiles) {
        const fileSize = file.size / (1024 * 1024 * 1024);
        if (!validMimetype(file)) {
          onError(
            multi ? t('En av filtyperna stöds ej') : t('Filtypen stöds ej')
          );
          break;
        } else if (fileSize > 10) {
          onError(
            multi
              ? t('Minst en av filerna är för stora. Max storlek är 10GB')
              : t('Filen är för stor. Max storlek är 10GB')
          );
          break;
        } else if (fileSize > 5) {
          onWarning(
            multi
              ? t(
                  'Minst en av filerna är stora. Det kan ta längre tid att ladda upp'
                )
              : t('Filen är stor. Det kan ta längre tid att ladda upp')
          );
          break;
        }
      }
    },
    [onUpload, onError, onWarning, rateLimitLeft, t, user?.workspace?.type]
  );

  useEffect(() => {
    if (!files) return;
    setFilesData(
      files.map((file) => {
        const fileSize = file.size / (1024 * 1024 * 1024);
        return {
          name: file.name,
          type: file.name.split('.').pop(),
          size:
            fileSize > 1
              ? `${fileSize.toFixed(1)} GB`
              : `${(fileSize * 1024).toFixed(1)} MB`,
        };
      })
    );
  }, [files]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: !isTrial,
    maxFiles: 100,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        ['.pptx'],
      'text/plain': ['.txt'],
      'application/rtf': ['.rtf'],

      'video/*': ['.mkv', '.mov'],
      'audio/*': ['.WMA', '.wma'],
      ...SUPPORTED_MIMETYPES.reduce((acc, type) => {
        acc[type] = [];
        return acc;
      }, {}),
      '*': ['.m4a'], // samsung recorder files can result in empty mimetype but are m4a files
    },
    disabled,
  });

  const handleRemove = useCallback(
    (e, idx) => {
      onUpload(files.filter((_, i) => i !== idx));
      setFilesData((d) => d.filter((_, i) => i !== idx));
      e.stopPropagation();
    },
    [onUpload, files]
  );

  if (uploading) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1rem;
        `}
      >
        {filesData.map((file, index) => (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
            `}
          >
            <div
              css={css`
                display: flex;
                gap: 0.5rem;
              `}
            >
              {progress[file.name]?.progress === 100 ? (
                <CheckIcon color="success" />
              ) : null}
              <Text variant="subtitle2" fontWeight="700">
                {file.name}
              </Text>
            </div>
            {progress[file.name]?.progress < 100 && (
              <React.Fragment>
                <LinearProgress
                  variant="determinate"
                  value={progress[file.name]?.progress}
                />
                <div
                  css={css`
                    display: flex;
                    gap: 1rem;
                    text-transform: uppercase;
                  `}
                >
                  <Text
                    variant="subtitle2"
                    color="text.secondary"
                    fontWeight="700"
                  >
                    {file.type}
                  </Text>
                  <Text
                    variant="subtitle2"
                    color="text.secondary"
                    fontWeight="700"
                  >
                    {file.size}
                  </Text>
                </div>
              </React.Fragment>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: ${isDragActive ? '#ECF3FF' : '#fff'};
        border-radius: 1rem;
        ${files?.length
          ? 'border: 2px solid var(--success);'
          : `border: 2px dashed ${
              isDragActive ? 'var(--primary-main)' : 'rgba(0, 0, 0, 0.26)'
            };`}

        margin-top: 1rem;
        padding: ${isDragActive || files?.length
          ? 'calc(1.375rem - 2px)'
          : 'calc(1.375rem - 2px)'};

        box-sizing: border-box;
        width: 100%;

        cursor: ${!disabled ? 'pointer' : 'cursor'};
        text-align: center;
      `}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      {!files?.length ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            flex-direction: column;
            color: ${disabled ? 'rgba(0, 0, 0, 0.26)' : 'var(--text-main)'};
          `}
        >
          <UploadFileOutlinedIcon
            fontSize="large"
            color={disabled ? 'rgba(0, 0, 0, 0.26)' : 'primary'}
            css={css`
              border: 2px solid
                ${!isDragActive ? 'white' : 'var(--primary-main)'};
              border-radius: 50%;
              padding: 1rem;
              margin-bottom: 0.5rem;
              color: ${!isDragActive ? 'rgba(0, 0, 0, 0.26)' : 'white'};
              background-color: ${!isDragActive
                ? 'white'
                : 'var(--primary-main)'};
            `}
          />
          <Text variant="body1" fontWeight="600">
            {t('Dra filer hit')}
          </Text>
          <Text
            variant="caption"
            css={css`
              color: #6c6c6c;
            `}
          >
            {t(
              'Dra och släpp en fil, eller klicka här för att ladda upp en fil från din dator.'
            )}
          </Text>
        </div>
      ) : (
        <div
          css={css`
            width: 100%;
            display: flex;
            flex-direction: column;
          `}
        >
          {filesData.map((file, index) => (
            <div
              key={index}
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;

                padding-top: 0.5rem;
                padding-bottom: 0.5rem;
                border-bottom: 1px solid var(--gray);
                &:last-child {
                  border-bottom: none;
                }
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  width: 100%;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 0.875rem;
                    background-color: var(--success-12p);

                    border-radius: 2rem;
                    padding: 4px;
                    padding-left: 14px;
                    padding-right: 14px;
                    box-sizing: border-box;
                  `}
                >
                  <CheckIcon
                    color="white"
                    fontSize="small"
                    css={css`
                      background-color: var(--success);
                      border-radius: 50%;
                    `}
                  />
                  <Text
                    variant="body2"
                    color="inherit"
                    fontWeight="700"
                    css={css`
                      color: #205723;
                      text-transform: uppercase;
                    `}
                  >
                    {t('Godkänd')}
                  </Text>
                </div>
                <IconButton
                  onClick={(e) => handleRemove(e, index)}
                  css={css`
                    margin-left: auto;
                  `}
                >
                  <DeleteIcon />
                </IconButton>
              </div>
              <Text variant="subtitle2" fontWeight="600" textAlign="left">
                {file.name}
              </Text>
              <div
                css={css`
                  display: flex;
                  gap: 1rem;
                  text-transform: uppercase;
                `}
              >
                <Text
                  variant="subtitle2"
                  color="text.secondary"
                  fontWeight="700"
                >
                  {file.type}
                </Text>
                <Text
                  variant="subtitle2"
                  color="text.secondary"
                  fontWeight="700"
                >
                  {file.size}
                </Text>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
